/**
 * AdminPromoCreditOrder — admin panel for manually crediting promo partners
 * for sales that happened off-tracker (DM, screenshot, word-of-mouth, or
 * pre-fix embed widget orders).
 *
 * Flow:
 *  1. Admin pastes the partner's tracking_code → we GET /api/admin/promo/lookup/{code}
 *  2. Page shows: partner card, event card, campaign commission rate, projected payout.
 *  3. List of uncredited orders on that event with checkboxes (default: all checked).
 *  4. "Preview" button → POST /api/promo/credit-order with dry_run=true → shows result.
 *  5. "Credit selected" button → same endpoint without dry_run → wallet credited.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Search, CheckCircle2, AlertTriangle, Loader2, DollarSign,
  Ticket, User, Sparkles, ChevronRight, Info,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { readableApiError } from '../utils/apiError';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('eventism_token') || ''}` } });
const fmtUSD = (n) => `$${(Number(n || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtCents = (c) => fmtUSD((c || 0) / 100);


export default function AdminPromoCreditOrder() {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [lookup, setLookup] = useState(null);
  const [selected, setSelected] = useState({});
  const [preview, setPreview] = useState(null);
  const [crediting, setCrediting] = useState(false);

  const handleLookup = async (e) => {
    e?.preventDefault?.();
    const tc = (code || '').trim();
    if (!tc) {
      toast.error('Paste a tracking code first');
      return;
    }
    setLoading(true);
    setLookup(null);
    setPreview(null);
    try {
      const r = await axios.get(`${API}/admin/promo/lookup/${encodeURIComponent(tc)}`, auth());
      setLookup(r.data);
      // Default-select every uncredited order.
      const sel = {};
      (r.data.uncredited_orders || []).forEach(o => { sel[o.order_id] = true; });
      setSelected(sel);
    } catch (err) {
      toast.error(readableApiError(err) || 'Lookup failed');
    } finally {
      setLoading(false);
    }
  };

  const selectedIds = lookup
    ? (lookup.uncredited_orders || []).filter(o => selected[o.order_id]).map(o => o.order_id)
    : [];

  const runCredit = async (dryRun) => {
    if (!lookup || selectedIds.length === 0) {
      toast.error('No orders selected');
      return;
    }
    setCrediting(true);
    setPreview(null);
    try {
      const r = await axios.post(
        `${API}/promo/credit-order`,
        {
          tracking_code: lookup.tracking_code,
          order_ids: selectedIds,
          dry_run: dryRun,
        },
        auth(),
      );
      setPreview(r.data);
      if (!dryRun) {
        toast.success(`Credited ${r.data.credited} order(s) to the partner`);
        // Refresh lookup so credited rows move out of the uncredited list.
        handleLookup();
      } else {
        toast.info(`Dry-run: ${r.data.matched} would credit · ${r.data.skipped} skipped`);
      }
    } catch (err) {
      toast.error(readableApiError(err) || 'Credit request failed');
    } finally {
      setCrediting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background pt-20 pb-16" data-testid="admin-credit-order">
      <div className="max-w-5xl mx-auto px-4 space-y-6">
        <header>
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">Promo Partners</div>
          <h1 className="text-3xl font-extrabold mt-1">Credit a sale to a partner</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-xl">
            Use this when a partner drove a sale that wasn&apos;t auto-attributed — pre-fix embed widget orders,
            DMs, screenshots, or word-of-mouth. Paste their tracking code to see every uncredited sale on
            their campaign.
          </p>
        </header>

        {/* ===== Lookup form ===== */}
        <Card>
          <CardContent className="p-5">
            <form onSubmit={handleLookup} className="flex flex-col sm:flex-row gap-3 items-stretch">
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="Paste tracking code (e.g. YWSUxjVq)"
                className="font-mono"
                data-testid="credit-tracking-input"
              />
              <Button type="submit" disabled={loading} data-testid="credit-lookup-btn">
                {loading
                  ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Looking up</>
                  : <><Search className="h-4 w-4 mr-2" /> Find sales</>}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* ===== Lookup results ===== */}
        {lookup && (
          <div className="space-y-4" data-testid="credit-lookup-result">
            {/* Partner + Event side-by-side */}
            <div className="grid md:grid-cols-2 gap-4">
              <Card>
                <CardContent className="p-5 space-y-2">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                    <User className="h-4 w-4" /> Partner
                  </div>
                  <p className="font-bold text-lg" data-testid="credit-partner-email">
                    {lookup.partner?.email || '—'}
                  </p>
                  {lookup.partner?.name && (
                    <p className="text-sm text-muted-foreground">{lookup.partner.name}</p>
                  )}
                  <p className="text-xs font-mono text-indigo-300 mt-2">
                    Tracking: {lookup.tracking_code}
                    <Badge variant="secondary" className="ml-2 text-[10px]">
                      {lookup.acceptance?.status || 'unknown'}
                    </Badge>
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-5 space-y-2">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                    <Ticket className="h-4 w-4" /> Event
                  </div>
                  <p className="font-bold text-lg" data-testid="credit-event-name">{lookup.event?.name || '—'}</p>
                  <p className="text-xs text-muted-foreground">
                    {lookup.event?.event_date}{lookup.event?.venue_name ? ` · ${lookup.event.venue_name}` : ''}
                  </p>
                  {lookup.campaign && (
                    <p className="text-xs text-muted-foreground mt-2">
                      Commission: <strong className="text-foreground">{lookup.campaign.commission_percent || 0}%</strong>
                      {lookup.campaign.commission_flat_cents
                        ? ` + ${fmtCents(lookup.campaign.commission_flat_cents)}/ticket`
                        : ''}
                    </p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Projected payout */}
            {(lookup.uncredited_orders || []).length > 0 && (
              <Card className="border-emerald-500/30 bg-emerald-500/5">
                <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3">
                    <Sparkles className="h-5 w-5 text-emerald-400" />
                    <div>
                      <p className="text-sm font-bold">
                        Project payout if you credit all selected:&nbsp;
                        <span className="text-emerald-300" data-testid="credit-projected-payout">
                          {fmtCents(lookup.projected_commission_cents)}
                        </span>
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Across {lookup.uncredited_orders.length} uncredited completed order{lookup.uncredited_orders.length === 1 ? '' : 's'}.
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => runCredit(true)}
                      disabled={crediting || selectedIds.length === 0}
                      data-testid="credit-dry-run-btn"
                    >
                      {crediting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Info className="h-4 w-4 mr-2" />}
                      Dry-run preview
                    </Button>
                    <Button
                      onClick={() => runCredit(false)}
                      disabled={crediting || selectedIds.length === 0}
                      data-testid="credit-confirm-btn"
                      className="bg-emerald-600 hover:bg-emerald-500"
                    >
                      {crediting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                      Credit {selectedIds.length} selected
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Uncredited orders list */}
            <Card>
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-sm uppercase tracking-wider text-muted-foreground">
                    Uncredited completed orders
                  </h3>
                  <span className="text-xs text-muted-foreground tabular-nums">
                    {selectedIds.length} of {(lookup.uncredited_orders || []).length} selected
                  </span>
                </div>
                {(lookup.uncredited_orders || []).length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center">
                    No uncredited completed orders on this event. Nothing to credit.
                  </p>
                ) : (
                  <div className="space-y-2" data-testid="credit-uncredited-list">
                    {lookup.uncredited_orders.map((o) => (
                      <label
                        key={o.order_id}
                        htmlFor={`o-${o.order_id}`}
                        className="flex items-center gap-3 p-3 rounded-lg border border-border bg-card hover:bg-muted/40 cursor-pointer transition-colors"
                        data-testid={`credit-row-${o.order_id}`}
                      >
                        <Checkbox
                          id={`o-${o.order_id}`}
                          checked={!!selected[o.order_id]}
                          onCheckedChange={(v) => setSelected((s) => ({ ...s, [o.order_id]: !!v }))}
                          data-testid={`credit-checkbox-${o.order_id}`}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold truncate">
                            {o.buyer_email || 'unknown buyer'}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {o.buyer_name || 'No name'} · {o.qty} ticket{o.qty === 1 ? '' : 's'} · {o.order_id}
                          </p>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-sm font-bold tabular-nums">{fmtUSD(o.total_amount)}</p>
                          <p className="text-[10px] text-muted-foreground">
                            {o.completed_at ? new Date(o.completed_at).toLocaleDateString() : ''}
                          </p>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Already credited to this partner */}
            {(lookup.already_credited_to_partner || []).length > 0 && (
              <Card className="border-emerald-500/30">
                <CardContent className="p-5">
                  <h3 className="font-bold text-sm uppercase tracking-wider text-emerald-300 mb-3 flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4" /> Already credited to this partner ({lookup.already_credited_to_partner.length})
                  </h3>
                  <div className="space-y-1.5">
                    {lookup.already_credited_to_partner.map((o) => (
                      <div key={o.order_id} className="flex items-center justify-between text-xs">
                        <span className="truncate">{o.buyer_email} · {o.order_id}</span>
                        <span className="tabular-nums text-emerald-300">
                          {fmtCents(o.promo_commission_cents)} commission
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Credited to a different partner */}
            {(lookup.credited_to_other_partner || []).length > 0 && (
              <Card className="border-amber-500/30">
                <CardContent className="p-5">
                  <h3 className="font-bold text-sm uppercase tracking-wider text-amber-300 mb-3 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4" /> Already credited to another partner ({lookup.credited_to_other_partner.length})
                  </h3>
                  <p className="text-xs text-muted-foreground mb-2">
                    These orders were already attributed to a different tracking code. They will be skipped.
                  </p>
                  <div className="space-y-1.5">
                    {lookup.credited_to_other_partner.map((o) => (
                      <div key={o.order_id} className="flex items-center justify-between text-xs">
                        <span className="truncate">{o.buyer_email} · {o.order_id}</span>
                        <span className="font-mono text-amber-300">{o.promo_code}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* ===== Result panel ===== */}
        {preview && (
          <Card className="border-indigo-500/40 bg-indigo-500/5" data-testid="credit-result-panel">
            <CardContent className="p-5 space-y-3">
              <h3 className="font-bold text-sm flex items-center gap-2 uppercase tracking-wider text-indigo-300">
                {preview.dry_run === undefined ? 'Result' : 'Dry-run preview'}
              </h3>
              <div className="grid grid-cols-3 gap-3 text-center">
                <ResultStat label="Matched" value={preview.matched} />
                <ResultStat label="Credited" value={preview.credited} accent="text-emerald-300" />
                <ResultStat label="Skipped" value={preview.skipped} accent="text-amber-300" />
              </div>
              {preview.details && preview.details.length > 0 && (
                <div className="space-y-1 max-h-64 overflow-y-auto">
                  {preview.details.map((d, i) => (
                    <div key={i} className="text-xs p-2 rounded border border-border bg-card flex items-center justify-between gap-2">
                      <span className="truncate">
                        <span className={`font-mono mr-2 ${d.action === 'credited' ? 'text-emerald-300' : d.action === 'would_credit' ? 'text-indigo-300' : 'text-amber-300'}`}>
                          {d.action}
                        </span>
                        {d.buyer_email} · {d.order_id}
                      </span>
                      {d.commission_cents != null && (
                        <span className="tabular-nums shrink-0">{fmtCents(d.commission_cents)}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}


function ResultStat({ label, value, accent }) {
  return (
    <div className="rounded-lg p-3 border border-border bg-card">
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</p>
      <p className={`text-2xl font-extrabold mt-1 tabular-nums ${accent || ''}`}>{value ?? 0}</p>
    </div>
  );
}
